import type { AxiosInstance } from 'axios';
import type {
  GetSystemNotificationsParams,
  GetSystemNotificationsResponse,
  GetUserDevicesParams,
  GetUserDevicesResponse,
  RegisterUserDeviceRequest,
  RegisterUserDeviceResponse,
  DeleteUserDeviceResponse,
  GetUserPreferencesParams,
  UserPreference,
  CreateUserPreferenceRequest,
  DeleteUserPreferenceParams,
  DeleteUserPreferenceResponse,
} from './contracts';

/**
 * Gateway for interacting with the Notifications API.
 */
export class NotificationsGateway {
  constructor(
    private readonly httpClient: AxiosInstance,
    private readonly baseUrl: string,
  ) {}

  /**
   * Returns the catalog of available system notifications with their types and metadata.
   * @param params - Optional filters by type slug or type ID.
   * @returns A list of system notifications and the total count.
   */
  async getSystemNotifications(params?: GetSystemNotificationsParams): Promise<GetSystemNotificationsResponse> {
    const { data } = await this.httpClient.get<GetSystemNotificationsResponse>(
      `${this.baseUrl}/notification/system-notifications`,
      { params },
    );
    return data;
  }

  /**
   * Lists push notification devices registered for the authenticated user.
   * @param params - Optional filters, pagination and service-token context.
   * @returns A list of user devices and the total count.
   */
  async getUserDevices(params?: GetUserDevicesParams): Promise<GetUserDevicesResponse> {
    const { data } = await this.httpClient.get<GetUserDevicesResponse>(`${this.baseUrl}/user/devices`, { params });
    return data;
  }

  /**
   * Registers or updates a push notification token for a user device.
   * Idempotent via findOrCreate on deviceToken.
   * @param payload - Device token and either deviceSlug or deviceId.
   * @returns The registered device summary.
   */
  async registerUserDevice(payload: RegisterUserDeviceRequest): Promise<RegisterUserDeviceResponse> {
    const { data } = await this.httpClient.post<RegisterUserDeviceResponse>(`${this.baseUrl}/user/devices`, payload);
    return data;
  }

  /**
   * Deletes a registered user device and its notification token.
   * @param id - UUID of the user device record.
   * @returns Confirmation of deletion.
   */
  async deleteUserDevice(id: string): Promise<DeleteUserDeviceResponse> {
    const { data } = await this.httpClient.delete<DeleteUserDeviceResponse>(`${this.baseUrl}/user/devices/${id}`);
    return data;
  }

  /**
   * Lists active notification preferences for the user, optionally filtered by device.
   * @param params - Optional filters and service-token context.
   * @returns A list of user preferences.
   */
  async getUserPreferences(params?: GetUserPreferencesParams): Promise<UserPreference[]> {
    const { data } = await this.httpClient.get<UserPreference[]>(`${this.baseUrl}/user/preferences`, { params });
    return data;
  }

  /**
   * Creates or activates a notification preference for a user device.
   * Idempotent via findOrCreate on the user/device/notification combination.
   * @param payload - Device ID and either systemNotificationSlug or systemNotificationId.
   * @returns The created or existing preference.
   */
  async createUserPreference(payload: CreateUserPreferenceRequest): Promise<UserPreference> {
    const { data } = await this.httpClient.post<UserPreference>(`${this.baseUrl}/user/preferences`, payload);
    return data;
  }

  /**
   * Deletes a notification preference by its ID.
   * @param id - UUID of the preference in `user_notification_preferences`.
   * @param params - Optional companyId/userId for Service Token context.
   * @returns Confirmation of deletion.
   */
  async deleteUserPreference(id: string, params?: DeleteUserPreferenceParams): Promise<DeleteUserPreferenceResponse> {
    const { data } = await this.httpClient.delete<DeleteUserPreferenceResponse>(
      `${this.baseUrl}/user/preferences/${id}`,
      { params },
    );
    return data;
  }
}

export type {
  SystemNotificationType,
  SystemNotification,
  GetSystemNotificationsParams,
  GetSystemNotificationsResponse,
  DeviceType,
  UserDevice,
  GetUserDevicesParams,
  GetUserDevicesResponse,
  RegisterUserDeviceRequest,
  RegisterUserDeviceResponse,
  DeleteUserDeviceResponse,
  UserPreferenceDevice,
  UserPreferenceSystemNotification,
  UserPreference,
  GetUserPreferencesParams,
  CreateUserPreferenceRequest,
  DeleteUserPreferenceParams,
  DeleteUserPreferenceResponse,
} from './contracts';
